import { TitleCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppSettingsService, CustomWindow, MetaDataService, slugify, WINDOW } from '@woolworthsnz/styleguide';
import { ProductDetailsResponse } from '@woolworthsnz/trader-api';

@Component({
	selector: 'product-seo',
	template: `<section [innerHTML]="generateJsonLdSchema(product)"></section>`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class SeoComponent implements OnInit {
	@Input() product: ProductDetailsResponse;

	constructor(
		private metaDataService: MetaDataService,
		private appSettings: AppSettingsService,
		private sanitizer: DomSanitizer,
		private router: Router,
		private titleCasePipe: TitleCasePipe,
		@Inject(WINDOW) private window: CustomWindow
	) {}

	ngOnInit(): void {
		this.setPageMeta(this.product);
	}

	public generateJsonLdSchema(product?: ProductDetailsResponse): SafeHtml | string {
		if (!product) {
			return ``;
		}

		const schema = this.mapProductToJsonSchema(product);
		if (!schema) {
			return ``;
		}

		return this.sanitizer.bypassSecurityTrustHtml(
			`<script type="application/ld+json">${JSON.stringify(schema, null, 2)}</script>`
		);
	}

	private setPageMeta(product: ProductDetailsResponse): void {
		const settingKey = 'productDetail';
		const defaultSettingKey = 'default';
		const name = this.titleCasePipe.transform(product?.name);
		const slugifiedProductName = slugify(product?.name);
		this.metaDataService.setPageMeta(
			name ? this.appSettings.getPageTitle(settingKey, name) : this.appSettings.getPageTitle(defaultSettingKey),
			name ? this.appSettings.getMetaTitle(settingKey, name) : this.appSettings.getMetaTitle(defaultSettingKey),
			name
				? this.appSettings.getMetaDescription(settingKey, name)
				: this.appSettings.getMetaDescription(defaultSettingKey),
			product?.images ? this.getImageUrls(product)[0] : this.appSettings.getMetaImage(defaultSettingKey),
			product?.sku
				? `${this.appSettings.getMetaUrl(defaultSettingKey)}/shop/productdetails?stockcode=${
						product.sku
				  }&name=${slugifiedProductName}`
				: this.appSettings.getMetaUrl(defaultSettingKey),
			{ productName: slugifiedProductName }
		);
	}

	generateNameForProduct(product: ProductDetailsResponse): string {
		let genericName: string = product.genericName || '';
		if (product.variety) {
			genericName = `${genericName} ${product.variety}`;
		}
		return this.titleCasePipe.transform(genericName.trim());
	}

	/* eslint-disable @typescript-eslint/naming-convention */
	private mapProductToJsonSchema(product: ProductDetailsResponse):
		| {
				[key: string]: string | string[] | undefined | { [key: string]: string | undefined | null };
		  }
		| string {
		const name = this.generateNameForProduct(product);
		if (!name) {
			return ``;
		}

		return {
			'@context': 'https://schema.org/',
			'@type': 'Product',
			name: name,
			image: this.getImageUrls(product),
			description: product.description,
			sku: product.sku,
			offers: {
				'@type': 'Offer',
				url: `${this.window.location.origin}${this.router.url}`,
				priceCurrency: 'NZD',
				price: this.getProductOfferPrice(product),
			},
			brand: {
				'@type': 'Brand',
				name: this.titleCasePipe.transform(product.brand),
			},
		};
		/* eslint-enable @typescript-eslint/naming-convention */
	}

	private getProductOfferPrice(product: ProductDetailsResponse): string {
		if (product.breadcrumb?.department?.name === 'Beer & Wine') {
			return String(product.price?.originalPrice);
		}
		return product.price?.savePrice && product.price?.savePrice > 0
			? String(product.price?.salePrice)
			: String(product.price?.originalPrice);
	}

	private getImageUrls(product: ProductDetailsResponse): string[] {
		const listOfUrls: string[] = [];

		const images = product.images;
		images?.forEach((element) => {
			if (element.big) {
				listOfUrls.push(element.big);
			}
			if (element.small) {
				listOfUrls.push(element.small);
			}
		});

		return listOfUrls;
	}
}
